import React, { useState, useMemo } from 'react';
import {
  X, Coins, Calendar, ShieldCheck, BarChart3, Send, Building2, Users, Eye, Lock,
  Leaf, Award, Zap, Plug, GraduationCap,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { getCompetitorLogo } from '../../data/competitorLogos';

/**
 * Tender application modal.
 *
 * 02/06/2026 — i18n FR/EN via translations.js (ao_modal_* keys). Narrative
 * strings (tender.narrative.announce, intel.advice, signerLabel) stay FR by
 * design — consistent with Messagerie memos (Claire/Nathalie/Marc).
 *
 * 05/02/2026 (Q1 Lot 2) — Refonte :
 *   • Suppression du sélecteur HSE 3-boutons (faible/modéré/fort) : le score
 *     d'engagement RSE est désormais calculé automatiquement depuis le state
 *     (panneau read-only `RseAutoCommitmentPanel`).
 *   • La probabilité de victoire s'affiche en **fourchette** (low–high) qui
 *     se resserre selon les rôles support recrutés (Commerce / Marketing / RH).
 *
 * Props :
 *   • computeProbabilityRange({ dailyPrice, durationDays })
 *       → { low, mid, high, spread, breakdown, supportFamilies }
 *   • Fallback : si non fourni, retombe sur `computeProbability` (legacy).
 */
export default function TenderApplicationModal({
  tender, intel, onClose, onSubmit, computeProbability, computeProbabilityRange,
}) {
  const { t } = useTranslation();
  const [dailyPrice, setDailyPrice] = useState(tender.baseDailyRevenue);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const priceMin = Math.round(tender.baseDailyRevenue * 0.70);
  const priceMax = Math.round(tender.baseDailyRevenue * 1.30);

  const range = useMemo(() => {
    try {
      if (typeof computeProbabilityRange === 'function') {
        const r = computeProbabilityRange({
          dailyPrice,
          durationDays: tender.durationDays,
        });
        return r || null;
      }
      // Legacy fallback : reconstruct a degenerate "range" centered on mid.
      const r = computeProbability({
        dailyPrice,
        durationDays: tender.durationDays,
      });
      const mid = r?.probability ?? 0.5;
      return {
        low: mid, mid, high: mid, spread: 0,
        breakdown: r?.breakdown || {},
        supportFamilies: [],
      };
    } catch (_e) {
      return { low: 0.5, mid: 0.5, high: 0.5, spread: 0, breakdown: {}, supportFamilies: [] };
    }
  }, [computeProbability, computeProbabilityRange, dailyPrice, tender.durationDays]);

  const breakdown = range.breakdown || {};
  const lowPct = Math.round((range.low ?? 0.5) * 100);
  const highPct = Math.round((range.high ?? 0.5) * 100);
  const midPct = Math.round((range.mid ?? 0.5) * 100);
  const rangeTone = midPct >= 65 ? 'text-emerald-300'
    : midPct >= 45 ? 'text-sky-300'
    : midPct >= 25 ? 'text-amber-300'
    : 'text-danger';

  const totalValue = dailyPrice * tender.durationDays;

  const handleSubmit = () => {
    setError(null);
    setSubmitting(true);
    try {
      // 05/02/2026 — L'engagement RSE est désormais auto-calculé côté slice.
      // On envoie quand même un champ par défaut pour la compatibilité.
      const res = onSubmit({ dailyPrice, durationDays: tender.durationDays, hseCommitment: 'auto' });
      if (!res?.ok) {
        setError(res?.reason || 'submit_failed');
        setSubmitting(false);
      }
    } catch (e) {
      setError(String(e?.message || e));
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-900/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="tender-modal-backdrop"
    >
      <div
        className="bg-ink-800 rounded-2xl w-full max-w-2xl border border-white/10 shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="tender-modal"
      >
        <header className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-mono mb-1">
              <Building2 className="w-3.5 h-3.5" />
              {t('ao_modal_kicker')}
            </div>
            <h2 className="text-xl font-display font-bold text-slate-100">
              {tender.clientName/* i18n-keep — proper noun */}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{tender.sector/* i18n-keep — taxonomy slug */}</p>
          </div>
          <button aria-label="Fermer"
            type="button"
            onClick={onClose}
            data-testid="tender-modal-close"
            className="p-1.5 rounded hover:bg-white/5 text-slate-400 hover:text-slate-200"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {/* Brief (narrative stays FR by design) */}
          <p className="text-sm text-slate-400 leading-relaxed bg-ink-700/30 rounded-lg p-3 border border-white/5">
            {tender.narrative.announce/* i18n-keep — narrative stays FR */}
          </p>

          {/* Fixed duration readout */}
          <div className="bg-ink-700/30 rounded-lg p-3 border border-white/5 flex items-center justify-between"
               data-testid="duration-fixed">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Calendar className="w-3.5 h-3.5" />
              <span>{t('ao_modal_duration_label')}</span>
            </div>
            <span className="font-mono text-emerald-300 font-bold text-base" data-testid="duration-value">
              {tender.durationDays} {t('ao_modal_duration_unit')}
            </span>
          </div>

          {/* Price lever */}
          <Lever
            icon={Coins}
            label={t('ao_modal_price_label')}
            value={dailyPrice}
            unit="€"
            min={priceMin}
            max={priceMax}
            step={5}
            onChange={setDailyPrice}
            hint={t('ao_modal_price_hint', { ref: tender.baseDailyRevenue, min: priceMin, max: priceMax })}
            testId="lever-price"
          />

          {/* Auto-RSE commitment panel (replaces former manual HSE 3-button selector) */}
          <RseAutoCommitmentPanel breakdown={breakdown} />

          {/* Competitive intelligence panel */}
          {intel?.signerLabel && (
            <CompetitiveIntelPanel intel={intel} t={t} />
          )}

          {/* Probability range readout */}
          <div className="bg-gradient-to-br from-slate-900/40 to-ink-700/40 rounded-xl p-4 border border-white/5">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-400 font-mono">
                <BarChart3 className="w-3.5 h-3.5" />
                Fourchette estimée{/* i18n-keep — FR UI label, narrative tone */}
              </div>
              <p className={`text-2xl font-display font-bold ${rangeTone}`} data-testid="tender-probability-range">
                <span data-testid="tender-probability-low">{lowPct}</span>
                <span className="text-sm text-slate-500 font-mono mx-1">–</span>
                <span data-testid="tender-probability-high">{highPct}</span>
                <span className="text-sm text-slate-500 font-mono ml-0.5">%</span>
              </p>
            </div>

            {/* Visual range bar */}
            <div className="relative h-2 bg-ink-800/60 rounded-full overflow-hidden mb-2">
              <div
                className={`absolute top-0 h-full ${
                  midPct >= 65 ? 'bg-emerald-500/60'
                  : midPct >= 45 ? 'bg-sky-500/60'
                  : midPct >= 25 ? 'bg-amber-500/60'
                  : 'bg-rose-500/60'
                }`}
                style={{ left: `${lowPct}%`, width: `${Math.max(1, highPct - lowPct)}%` }}
                data-testid="tender-range-bar"
              />
              <div
                className="absolute top-0 h-full w-0.5 bg-white/80"
                style={{ left: `${midPct}%` }}
                data-testid="tender-range-mid"
              />
            </div>

            {/* Support family contribution hint */}
            <SupportFamilyHint families={range.supportFamilies || []} t={t} />

            {/* Hidden mid for backwards-compat tests */}
            <span data-testid="tender-probability" className="sr-only">{midPct}</span>

            <div className="grid grid-cols-3 gap-1 text-[10px] text-slate-500 mt-2 font-mono">
              {/* i18n-keep — compact factor labels stay stable across FR/EN */}
              <BreakdownChip label="Rép." value={breakdown.reputation} /* i18n-keep — compact chip label */ />
              <BreakdownChip label="Prix" value={breakdown.price} />
              <BreakdownChip label="RSE" value={breakdown.rse} />
              <BreakdownChip label="Flotte" value={breakdown.fleet} />
              <BreakdownChip label="Fin." value={breakdown.financial} />
              <BreakdownChip label="HSE" value={breakdown.hseCommitment} />
            </div>
            <p className="text-[10px] text-slate-500 italic mt-2">
              {t('ao_modal_total_value')} : <strong className="text-slate-300">{totalValue.toLocaleString('fr-FR')} €</strong>
            </p>
          </div>

          {error && (
            <div className="text-xs text-danger bg-danger/10 border border-danger/30 rounded p-2" data-testid="tender-error">
              {t('ao_modal_error_prefix')} : {error/* i18n-keep — runtime error code */}
            </div>
          )}
        </div>

        <footer className="p-5 border-t border-white/5 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            data-testid="tender-cancel"
            className="px-4 py-2 rounded text-sm text-slate-400 hover:text-slate-200"
          >
            {t('ao_modal_cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="tender-submit"
            className="px-5 py-2 rounded bg-emerald-600/20 text-emerald-200 border border-emerald-500/50 text-sm font-semibold hover:bg-emerald-600/30 disabled:opacity-50 flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            {t('ao_modal_submit')}
          </button>
        </footer>
      </div>
    </div>
  );
}

function RseAutoCommitmentPanel({ breakdown }) {
  const auto = breakdown?.rseAutoBreakdown || {};
  const score = breakdown?.rseAutoScore ?? 0;
  const level = breakdown?.rseAutoLevel || 'none';
  const levelLabel = level === 'fort' ? 'Fort' // i18n-keep — FR levels
    : level === 'modéré' ? 'Modéré' // i18n-keep
    : level === 'standard' ? 'Standard'
    : 'Aucun'; // i18n-keep
  const levelTone = level === 'fort' ? 'text-emerald-300 bg-emerald-900/30 border-emerald-500/40'
    : level === 'modéré' ? 'text-sky-300 bg-sky-900/30 border-sky-500/40' // i18n-keep — value match
    : level === 'standard' ? 'text-amber-300 bg-amber-900/30 border-amber-500/40'
    : 'text-slate-400 bg-slate-700/30 border-slate-500/40';

  const lines = [
    { key: 'claire', icon: Award, label: 'Claire Maréchal (QHSE)', value: auto.claire || 0, max: 5 }, // i18n-keep
    { key: 'electricCharger', icon: Plug, label: 'Borne recharge VE', value: auto.electricCharger || 0, max: 3 },
    { key: 'electricFleet', icon: Zap, label: 'Flotte électrique', value: auto.electricFleet || 0, max: 3 }, // i18n-keep
    { key: 'ecoVadis', icon: Leaf, label: 'EcoVadis (audit RSE)', value: auto.ecoVadis || 0, max: 4 },
    { key: 'rseTraining', icon: GraduationCap, label: 'Chauffeurs formés RSE', value: auto.rseTraining || 0, max: 2 }, // i18n-keep
  ];

  return (
    <div className="bg-ink-700/30 rounded-lg p-3 border border-white/5" data-testid="rse-auto-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Engagement RSE (auto){/* i18n-keep — FR label, narrative tone */}</span>
        </div>
        <span
          className={`text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded border ${levelTone}`}
          data-testid="rse-auto-level"
        >
          {levelLabel} · <strong data-testid="rse-auto-score">{score}</strong>/15
        </span>
      </div>
      <div className="grid grid-cols-1 gap-1.5">
        {lines.map(line => {
          const Icon = line.icon;
          const active = line.value > 0;
          return (
            <div
              key={line.key}
              className={`flex items-center justify-between text-[11px] px-2 py-1 rounded border ${
                active
                  ? 'bg-emerald-900/15 border-emerald-500/20 text-emerald-100'
                  : 'bg-ink-800/40 border-white/5 text-slate-500'
              }`}
              data-testid={`rse-auto-line-${line.key}`}
            >
              <span className="flex items-center gap-1.5">
                <Icon className="w-3 h-3" />
                {line.label/* i18n-keep — French label */}
              </span>
              <span className="font-mono">
                {active ? `+${line.value}` : '0'}/{line.max}
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-500 italic mt-2">
        {/* i18n-keep — explanatory FR copy, narrative tone (Messagerie memos) */}
        {`Le score d'engagement RSE est désormais calculé automatiquement (plus de choix manuel). Pour l'augmenter : recruter Claire Maréchal, investir dans une borne de recharge, électrifier la flotte, viser l'audit EcoVadis et former vos chauffeurs.`/* i18n-keep — line marker */}
      </p>
    </div>
  );
}

function SupportFamilyHint({ families, t: _t }) {
  // i18n-keep — FR labels for support family names (narrative tone)
  const labelMap = {
    commerce: 'Commercial',
    marketing: 'Marketing',
    rh: 'RH',
  };
  const allFamilies = ['commerce', 'marketing', 'rh'];
  const missing = allFamilies.filter(f => !families.includes(f));
  if (families.length === 3) {
    return (
      <p
        className="text-[10px] text-emerald-300 italic"
        data-testid="support-family-hint-full"
      >
        {/* i18n-keep — FR narrative hint */}
        Estimation très précise — Commercial, Marketing &amp; RH recrutés.
      </p>
    );
  }
  return (
    <p
      className="text-[10px] text-slate-400 italic"
      data-testid="support-family-hint-partial"
    >
      {/* i18n-keep — FR narrative hint, dynamic list */}
      Fourchette large — recrutez{' '}
      {missing.map((f, i) => (
        <span key={f}>
          {i > 0 && (i === missing.length - 1 ? ' et ' : ', ')}
          <strong className="text-slate-200">{labelMap[f]}</strong>
        </span>
      ))}
      {' '}{`pour resserrer l'estimation.`/* i18n-keep — FR narrative hint */}
    </p>
  );
}

function CompetitiveIntelPanel({ intel, t }) {
  const { unlocked, signerLabel, competitors, priceRange, advice } = intel;
  return (
    <div
      className={`rounded-lg p-3 border ${unlocked
        ? 'bg-sky-950/30 border-sky-500/30'
        : 'bg-slate-700/20 border-slate-500/20'}`}
      data-testid="tender-intel-panel"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-mono mb-2">
        {unlocked
          ? <Eye className="w-3.5 h-3.5 text-sky-300" />
          : <Lock className="w-3.5 h-3.5 text-slate-500" />}
        <span className={unlocked ? 'text-sky-300' : 'text-slate-500'}>
          {t('ao_modal_intel_title')}
        </span>
      </div>

      {competitors.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="tender-intel-competitors">
          <Users className="w-3 h-3 text-slate-400" />
          {competitors.map((name, i) => {
            const logo = getCompetitorLogo(name);
            return (
              <span
                key={`${name}-${i}`}
                className="inline-flex items-center gap-1 text-[10px] bg-ink-800/60 border border-white/10 rounded px-1.5 py-0.5 text-slate-300"
                data-testid={`tender-competitor-${i}`}
              >
                {logo ? (
                  <img
                    src={logo}
                    alt=""
                    className="w-3.5 h-3.5 rounded-sm object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <span className="w-3.5 h-3.5 rounded-sm bg-slate-600 text-[10px] flex items-center justify-center text-white font-bold uppercase">
                    {(name || '?').slice(0, 1)}
                  </span>
                )}
                {name/* i18n-keep — competitor proper noun */}
              </span>
            );
          })}
        </div>
      )}

      {unlocked && priceRange && (
        <div className="text-[11px] text-slate-300 mb-2" data-testid="tender-intel-price-range">
          {t('ao_modal_competitor_range')}{' '}
          <strong className="text-sky-200 font-mono">{priceRange[0]} – {priceRange[1]} €/j</strong>
        </div>
      )}

      <p className={`text-[11px] leading-relaxed ${unlocked ? 'text-slate-300' : 'text-slate-500 italic'}`}>
        <MarkdownInline text={advice/* i18n-keep — advice voice stays FR, consistent with Messagerie memos */} />
      </p>
      <p className="text-[10px] text-slate-500 italic mt-2 text-right">
        — {signerLabel/* i18n-keep — signer voice stays FR */}
      </p>
    </div>
  );
}

function MarkdownInline({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (/^\*\*[^*]+\*\*$/.test(p)) {
          return <strong key={`b-${i}-${p.slice(2, 12)}`} className="text-slate-100">{p.slice(2, -2)}</strong>;
        }
        return <span key={`s-${i}-${p.slice(0, 12)}`}>{p}</span>;
      })}
    </>
  );
}

function Lever({ icon: Icon, label, value, unit, min, max, step, onChange, hint, testId }) {
  return (
    <div className="bg-ink-700/30 rounded-lg p-3 border border-white/5">
      <div className="flex items-center justify-between text-xs text-slate-400 mb-1.5">
        <span className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" />
          {label}
        </span>
        <span className="font-mono text-emerald-300 font-bold text-base" data-testid={`${testId}-value`}>
          {value} {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-emerald-500"
        data-testid={testId}
      />
      <p className="text-[10px] text-slate-500 italic mt-1">{hint}</p>
    </div>
  );
}

function BreakdownChip({ label, value }) {
  const v = value || 0;
  const sign = v > 0 ? '+' : '';
  const tone = v > 0 ? 'text-emerald-400' : v < 0 ? 'text-rose-400' : 'text-slate-500';
  return (
    <div className="flex justify-between bg-ink-800/40 rounded px-1.5 py-0.5">
      <span>{label}</span>
      <span className={tone}>{sign}{(v * 100).toFixed(0)}</span>
    </div>
  );
}
